import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

interface FormData {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
}

const categories = [
  { value: "technical", label: "Problème Technique" },
  { value: "account", label: "Compte & Profil" },
  { value: "community", label: "Communautés" },
  { value: "content", label: "Contenu & Modération" },
  { value: "tokens", label: "Tokens & Récompenses" },
  { value: "feature", label: "Suggestion de Fonctionnalité" },
  { value: "other", label: "Autre" }
];

const quickLinks = [
  {
    title: "Centre d'Aide",
    description: "Consultez notre FAQ et guides",
    icon: "📚",
    link: "/help"
  },
  {
    title: "Communauté",
    description: "Rejoignez la discussion",
    icon: "💬",
    link: "/community-dao"
  },
  {
    title: "À propos du J.U.P",
    description: "Découvrez notre vision",
    icon: "🌟",
    link: "/about-jup"
  }
];

const Support: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simuler l'envoi du formulaire
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Support request:', formData);
    setSubmitted(true);
    setIsSubmitting(false);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        category: '',
        subject: '',
        message: ''
      });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyan-400/20 rounded-full mb-6 border-2 border-cyan-400">
            <svg className="h-10 w-10 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h1 className="text-5xl font-extrabold text-emerald-100 mb-4">
            Support Jupipotes
          </h1>
          <p className="text-xl text-emerald-200">
            Notre équipe est là pour vous aider
          </p>
        </div>

        {/* Quick Links */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-cyan-300 mb-6 text-center">
            Avant de nous contacter...
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickLinks.map((item, idx) => (
              <Link
                key={idx}
                to={item.link}
                className={`${glassCard} hover:border-cyan-400/50 transition-all duration-300 text-center group`}
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-emerald-100 mb-2 group-hover:text-cyan-300 transition-colors">
                  {item.title}
                </h3>
                <p className="text-emerald-200 text-sm">{item.description}</p>
              </Link>
            ))}
          </div>
        </section>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Formulaire de Contact */}
          <div className="md:col-span-2">
            <div className={glassCard}>
              <h2 className="text-2xl font-bold text-emerald-100 mb-6 flex items-center gap-3">
                <svg className="h-7 w-7 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contactez-nous
              </h2>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6 border-2 border-green-400">
                    <svg className="h-10 w-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-emerald-100 mb-3">Message envoyé !</h3>
                  <p className="text-emerald-200 mb-6">
                    Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
                  </p>
                  <p className="text-cyan-300 text-sm">
                    Temps de réponse moyen : 24-48 heures
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-emerald-200 font-semibold mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-emerald-400/30 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        placeholder="Jean Dupont"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-emerald-200 font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-emerald-400/30 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        placeholder="jean@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-emerald-200 font-semibold mb-2">
                      Catégorie *
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-emerald-400/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    >
                      <option value="" className="bg-gray-800">Sélectionnez une catégorie</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value} className="bg-gray-800">
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-emerald-200 font-semibold mb-2">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-emerald-400/30 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      placeholder="Résumé de votre demande"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-emerald-200 font-semibold mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-white/10 border border-emerald-400/30 rounded-lg text-white placeholder-emerald-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 resize-none"
                      placeholder="Décrivez votre problème ou votre question en détail..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                      isSubmitting
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Envoyer le message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Infos de contact et FAQ rapide */}
          <div className="space-y-6">
            {/* Temps de réponse */}
            <div className={glassCard}>
              <h3 className="text-lg font-bold text-emerald-100 mb-4 flex items-center gap-2">
                <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Temps de réponse
              </h3>
              <div className="space-y-3 text-emerald-200 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  <span><strong>Urgent:</strong> 2-6 heures</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full" />
                  <span><strong>Normal:</strong> 24-48 heures</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-400 rounded-full" />
                  <span><strong>Suggestion:</strong> 3-5 jours</span>
                </div>
              </div>
            </div>

            {/* Autres moyens de contact */}
            <div className={glassCard}>
              <h3 className="text-lg font-bold text-emerald-100 mb-4">
                Autres moyens de contact
              </h3>
              <div className="space-y-4">
                <Link
                  to="/communities"
                  className="flex items-center gap-3 p-3 bg-emerald-900/20 rounded-lg hover:bg-emerald-900/40 transition-all duration-300"
                >
                  <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  <div>
                    <p className="text-emerald-100 font-semibold">Forum</p>
                    <p className="text-emerald-300 text-xs">Discutez avec la communauté</p>
                  </div>
                </Link>
                
                <div className="flex items-center gap-3 p-3 bg-cyan-900/20 rounded-lg">
                  <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-emerald-100 font-semibold">Email</p>
                    <p className="text-cyan-300 text-xs">support@jupipotes.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Rapide */}
            <div className={glassCard}>
              <h3 className="text-lg font-bold text-emerald-100 mb-4">
                Questions fréquentes
              </h3>
              <div className="space-y-3 text-sm">
                <Link to="/help" className="block text-cyan-300 hover:text-cyan-200 transition-colors">
                  → Comment créer un compte ?
                </Link>
                <Link to="/help" className="block text-cyan-300 hover:text-cyan-200 transition-colors">
                  → Comment rejoindre une communauté ?
                </Link>
                <Link to="/help" className="block text-cyan-300 hover:text-cyan-200 transition-colors">
                  → Comment obtenir des tokens JUP ?
                </Link>
                <Link to="/help" className="block text-cyan-300 hover:text-cyan-200 transition-colors">
                  → Comment signaler un problème ?
                </Link>
              </div>
              <Link
                to="/help"
                className="mt-4 block text-center py-2 bg-cyan-400/20 text-cyan-300 rounded-lg hover:bg-cyan-400/30 transition-all duration-300 font-semibold"
              >
                Voir toute la FAQ
              </Link>
            </div>
          </div>
        </div>

        {/* Retour */}
        <div className="mt-12 text-center">
          <Link
            to="/JUPResources"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux ressources
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Support;
